import React, { useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { CategoryForm } from '@traquility/ui/diary';
import type { CategoryFormData, CategoryFormLabels } from '../shared/types/categoryForm';

export interface CategoryFormContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  initialData: CategoryFormData;
  backUrl: string;
  successUrl: string;
  labels: CategoryFormLabels;
  errors?: Record<string, string[]>;
}

const CREATE_CATEGORY_MUTATION = `
  mutation CreateCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      category {
        id
        name
        position
      }
      errors
    }
  }
`;

const UPDATE_CATEGORY_MUTATION = `
  mutation UpdateCategory($input: UpdateCategoryInput!) {
    updateCategory(input: $input) {
      category {
        id
        name
        position
      }
      errors
    }
  }
`;

function CategoryFormContainer({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
}: CategoryFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (data: CategoryFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const isEdit = !!data.id;
    const mutation = isEdit ? UPDATE_CATEGORY_MUTATION : CREATE_CATEGORY_MUTATION;
    const variables = isEdit
      ? { input: { id: data.id, name: data.name } }
      : { input: { name: data.name } };

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: mutation,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error('Network error');
      }

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateCategory : result.data.createCategory;

      if (mutationResult.errors && mutationResult.errors.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      // Success - redirect to success URL
      window.location.href = successUrl;
    } catch (error) {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const mode = initialData.id ? 'edit' : 'new';

  return (
    <CategoryForm
      initialData={initialData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      errors={errors}
      labels={labels}
      mode={mode}
    />
  );
}

// Store root reference for cleanup
let formRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountCategoryForm(
  container: HTMLElement,
  props: CategoryFormContainerProps
): Root {
  if (formRoot) {
    formRoot.unmount();
  }

  const root = createRoot(container);
  formRoot = root;
  root.render(<CategoryFormContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountCategoryForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default CategoryFormContainer;
